import { startOfToday } from "date-fns";
import { DayPicker as DayPickerBase, DayPickerProps } from "react-day-picker";
import styled from "styled-components";
import {
  shade0,
  shade300,
  shade600,
  shade850,
  shade1000,
} from "../../styles/tokens";
import { normalStyles, smallStyles } from "../body/Body.Parts";
import { Skeleton } from "../skeleton/Skeleton.Parts";

// TEXT

// STYLES
export const DayPicker = styled(DayPickerBase)`
  --rdp-cell-size: 3rem;
  --rdp-month-gap: 3rem;

  display: inline-block;
  position: relative;
  color: ${shade0};

  .rdp-months {
    display: flex;
    flex-wrap: wrap;
    gap: var(--rdp-month-gap);
    justify-content: center;
  }

  .rdp-month {
    display: flex;
    flex-flow: column;
    gap: 1rem;
  }

  .rdp-month_caption {
    ${normalStyles};
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2rem;
    text-align: center;
  }

  .rdp-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: absolute;
    inset: 0 0 auto 0;
    pointer-events: none;
  }

  .rdp-button_previous,
  .rdp-button_next {
    pointer-events: auto;
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: ${shade0};
    cursor: pointer;
    border-radius: 50%;

    &:hover:not([aria-disabled="true"]) {
      background: ${shade850};
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px ${shade0};
    }

    &[aria-disabled="true"] {
      color: ${shade600};
      cursor: not-allowed;
    }
  }

  .rdp-month_grid {
    border-collapse: collapse;
  }

  .rdp-weekdays {
    display: grid;
    grid-template-columns: repeat(7, var(--rdp-cell-size));
  }

  .rdp-weekday {
    ${smallStyles};
    font-weight: 400;
    color: ${shade300};
    text-align: center;
    text-transform: uppercase;
    height: var(--rdp-cell-size);
    line-height: var(--rdp-cell-size);
  }

  .rdp-weeks {
    display: flex;
    flex-flow: column;
  }

  .rdp-week {
    display: grid;
    grid-template-columns: repeat(7, var(--rdp-cell-size));
  }

  .rdp-day {
    ${normalStyles};
    width: var(--rdp-cell-size);
    height: var(--rdp-cell-size);
    padding: 0;
    text-align: center;
  }

  .rdp-day_button {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    border-radius: 50%;
    color: inherit;
    cursor: pointer;
    font: inherit;

    &:hover:not(:disabled) {
      box-shadow: 0 0 0 1px ${shade0} inset;
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px ${shade0} inset;
    }

    &:disabled {
      color: ${shade600};
      cursor: not-allowed;
    }
  }

  .rdp-selected .rdp-day_button {
    background: ${shade0};
    color: ${shade1000};
  }

  .rdp-range_middle {
    background: ${shade850};
  }

  .rdp-range_middle .rdp-day_button {
    background: transparent;
    border-radius: 0;
    color: ${shade0};

    &:hover:not(:disabled) {
      background: ${shade600};
    }
  }

  .rdp-range_start,
  .rdp-range_end {
    background: ${shade850};
  }

  .rdp-range_start {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
  }

  .rdp-range_end {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }

  .rdp-range_start.rdp-range_end {
    background: transparent;
  }

  .rdp-range_start .rdp-day_button,
  .rdp-range_end .rdp-day_button {
    background: ${shade0};
    color: ${shade1000};
  }

  .rdp-hidden {
    visibility: hidden;
  }
`;

export const StoryWrapper = styled.div`
  display: flex;
  flex-flow: column;
  gap: 3rem;
`;

export const WrapperSkeleton = styled.div`
  display: inline-flex;
  gap: 3rem;
`;

export const HeadingSkeleton = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

export const ChevronSkeleton = styled(Skeleton)<{ $show: boolean }>`
  visibility: ${({ $show }) => ($show ? "visible" : "hidden")};
`;

export const CalendarSkeleton = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 21rem;
  gap: 1rem;
`;

export const WeekSkeleton = styled(Skeleton)`
  margin-bottom: 0.25rem;
`;

// DATA

// TYPES
export type Props = DayPickerProps;

export interface SkeletonProps extends Pick<Props, "numberOfMonths"> {
  numberOfWeeks?: number;
}

// CONSTANTS
export const DEFAULT_SKELETON_MONTHS = 1;
export const DEFAULT_SKELETON_WEEKS = 6;

// FUNCTIONS
export const getMonthBounds = (props: Props) => {
  const { disabled, startMonth, endMonth, month, defaultMonth } = props;
  const bounds = { startMonth, endMonth };

  // Full disable should override any bounds to prevent navigation...
  if (disabled === true)
    bounds.startMonth = bounds.endMonth =
      month ?? defaultMonth ?? startOfToday();

  // If disabled is a date interval, we can infer missing bounds from it...
  if (typeof disabled === "object") {
    if (!startMonth && "before" in disabled)
      bounds.startMonth = disabled.before;
    if (!endMonth && "after" in disabled) bounds.endMonth = disabled.after;
  }

  // Note: inferring bounds when the disabled prop is an array of Matchers seemed
  // too complex to be worth the effort, and it's not a common use case, so we skip it.
  // Use explicit start and end month props in that case.

  return bounds;
};

// ASSETS
